import CartService from '@/services/isomorphic/CartService';
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import CartVM from '../view-models/CartVM';
import { usePending } from './usePending';
import type IPurchasableProductVM from '../view-models/IPurchasableProductVM';

/**
 * A function which "completes" the ad hoc product cart, which
 * reinitializes the cart state.
 *
 * It is expected to be called after a cart has been successfully placed.
 */
export type CompleteAdHocCartFn = () => void;

/**
 * A function which "completes" the single product cart, which
 * resets the cart state.
 *
 * It is expected to be called after a cart has been successfully placed.
 *
 * **Note**: The caller can optionally specify if the action should
 * skip creating a new cart. This is primarily a performance optimization
 * since we always navigate away after order placement, and skipping
 * the creation of the new cart avoids delaying the navigation. However,
 * not skipping is technically the "correct" behavior, so it is done by default.
 */
export type CompleteSingleProductCartFn = (args?: {
  skipNewCart?: boolean;
}) => void;

/**
 * A hook that manages a temporary cart for a single product to be purchased.
 * @param productVM - The product view model backing the product to be purchased.
 * @returns A tuple containing the ad-hoc cart VM and a boolean indicating if cart updates are pending.
 */
export function useSingleProductCart(
  productVM: IPurchasableProductVM
): [CartVM, CompleteSingleProductCartFn, boolean] {
  const [skippedNewCart, setSkippedNewCart] = useState<CartVM | null>(null);

  const [cart, completeAdHocCart] = useInitializeAdHocCart();
  const isPending = useSyncCurrentProductWithCart(productVM, cart);

  const completeSingleProductCart = useCallback<CompleteSingleProductCartFn>(
    function completeSingleProductCart(args) {
      if (args?.skipNewCart) {
        setSkippedNewCart(new CartVM());
      } else {
        completeAdHocCart();
      }
    },
    [completeAdHocCart]
  );

  return [skippedNewCart ?? cart, completeSingleProductCart, isPending];
}

/**
 * A hook which initializes an ad-hoc cart view model.
 * @returns A cart view model.
 */
export function useInitializeAdHocCart(): [CartVM, CompleteAdHocCartFn] {
  /**
   * **Implementation Note**: Although the initial cart in state is "{@link CartVM.isReady not ready}",
   * we use this cart as a placeholder to show the dependent UI as soon as possible,
   * even if it can't be interacted with yet.
   *
   * The effect below is responsible for initializing the ad-hoc cart model, and updating the
   * state with a "ready" cart. **Note**: It's not possible to defer ad-hoc cart creation to
   * after the button is clicked because some APIs (such as Apple Pay) require that the event
   * handler logic executes synchronously. As a result, the cart must be ready _before_ the
   * button is clicked.
   */
  const [cart, setCart] = useState<CartVM>(() => new CartVM());

  const completionCountRef = useRef(0);
  const completeCheckoutCart = useCallback(
    async function completeCheckoutCart() {
      const completionCount = completionCountRef.current++;
      startTransition(async () => {
        const newCart = await CartService.getNewAdHocCart();
        // Prevent race condition if `completeCheckoutCart()` was called multiple times
        if (completionCount === completionCountRef.current) {
          startTransition(() => {
            setCart(new CartVM(newCart));
          });
        }
      });
    },
    []
  );

  useEffect(function initializeAdHocCart() {
    let isStale = false;
    const fetchAdHocCart = async (): Promise<void> => {
      const cartModel = await CartService.getNewAdHocCart();

      if (isStale) return;
      setCart(new CartVM(cartModel));
    };

    /* eslint-disable-next-line no-void --
      effects must be synchronous. We just need to kick off the request. */
    void fetchAdHocCart();

    return () => {
      isStale = true;
      /**
       * According to the {@link https://docs.talon.one/docs/dev/concepts/entities/customer-sessions Talon.One docs}, it is safe to leave a session
       * open indefinitely, such as when the customer never proceeds to
       * checkout. Therefore, we don't need to clean up the cart session.
       */
    };
  }, []);

  return [cart, completeCheckoutCart];
}

/**
 * A hook that manages a single item cart.
 * @param productVM - The product view model backing the product to be purchased.
 * @param cart - A cart to be synchronized with the current product.
 * @returns A boolean indicating if cart updates are pending.
 */
function useSyncCurrentProductWithCart(
  productVM: IPurchasableProductVM,
  cart: CartVM
): boolean {
  const [isPending, addPending] = usePending();

  const { isReady } = cart;
  const { isPurchasable, model } = productVM;

  useEffect(
    function syncCurrentProductWithAdHocCart() {
      if (!isReady) return;

      if (!isPurchasable) {
        if (cart.items.length > 0) {
          addPending(cart.removeItem(cart.items[0]));
        }
        return;
      }

      /**
       * If the ad-hoc cart is empty, add the product to the cart.
       * Otherwise, replace the existing product in the cart with the new product.
       *
       * Although "replace" doesn't have the ability to update the quantity of the product
       * (to assert that the quantity is 1), this is a safe operation because we make sure
       * to only add a single product in the beginning.
       */
      if (cart.items.length === 0) {
        addPending(cart.addItem(model, 1));
      } else {
        addPending(cart.replaceLineItem(cart.items[0], model));
      }
    },
    [isReady, isPurchasable, cart, addPending, model]
  );

  return isPending;
}
